// src/modules/app-views/_apps/appmulti/quotations/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Affix, Button, Empty, notification, Row, Space, Spin } from 'antd';
import { HEADER_HEIGHT } from 'constants/ThemeConstant';
import { TagsOutlined } from '@ant-design/icons';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import { MultiQuotationsHeader, CardPlan } from '../../Components';
import MultiQuotationCompareModal from '../../List/Modals/CompareModal';
import QuotationsFilter from '../../List/QuotationsFilter';
import MultiQuotationCheckoutModal from '../../Form/Form';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const selectedModule = { icon: TagsOutlined, title: 'quotations' };

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const QuotationsInit = props => {
  // HOOKS
  const isMobile = appService.isMobile();
  const subscription = useZustand('auth', 'subscription');
  const [api, contextHolder] = notification.useNotification();

  // STATE — search results (replaces legacy documentContext)
  const [listPlans, setListPlans] = useState([]);
  const [filter, setFilter] = useState(null);
  const [isWorking, setIsWorking] = useState(false);

  // STATE — compare
  const [comparePlans, setComparePlans] = useState([]);
  const [compare, setCompare] = useState([]);
  const [compareColumns, setCompareColumns] = useState([]);
  const [compareData, setCompareData] = useState([]);

  // STATE — selection + modal flags
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  // STATE — checkout (travellers + buyer shared with the wizard)
  const [travellers, setTravellers] = useState([]);
  const [buyer, setBuyer] = useState(null);

  // STATE — sorter/filter
  const [filteredData, setFilteredData] = useState([]);
  const [classification, setClassification] = useState('best_seller');
  const [travelProfile, setTravelProfile] = useState(null);
  const [operator, setOperator] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);

  // EFFECTS
  useEffect(() => {
    closeNotification(true);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFilteredData(listPlans);
    setClassification('best_seller');
    setTravelProfile(null);
    setOperator(null);
  }, [listPlans]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (compare.length > 1) {
      openNotification();
    } else {
      closeNotification();
    }
  }, [compare]); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLERS
  // bridges the header's legacy setDocumentContext({listPlans, filter}) call to local state
  const setDocumentContext = ctx => {
    if (ctx?.listPlans !== undefined) setListPlans(ctx.listPlans);
    if (ctx?.filter !== undefined) setFilter(ctx.filter);
  };

  const closeNotification = forceClear => {
    api.destroy('compare-box');
    if (forceClear) {
      setCompare([]);
      setComparePlans([]);
      setCompareData([]);
      setCompareColumns([]);
    }
  };

  const onClickComparePlans = () => {
    closeNotification();
    setShowCompareModal(true);
  };

  const openNotification = () => {
    const key = 'compare-box';
    const btn = (
      <Space>
        <Button type="link" size="small" onClick={() => closeNotification(true)}>
          Cancelar
        </Button>
        <Button type="primary" size="small" onClick={onClickComparePlans}>
          Comparar
        </Button>
      </Space>
    );
    api.info({
      message: 'Compare os planos selecionados',
      duration: 0,
      closeIcon: null,
      btn,
      key,
      onClose: closeNotification,
    });
  };

  // 1d-iii mounts MultiQuotationCheckoutModal; here we only set selection + flag
  const onClickOpenCheckoutModal = plan => {
    closeNotification(true);
    setSelectedRecord(plan);
    setShowCheckoutModal(true);
  };

  // RENDER
  const childProps = {
    ...props,
    selectedModule,
    isWorking,
    setIsWorking,
    document: undefined,
    setDocumentContext,
    subscription,
    filter,
    onClickOpenCheckoutModal,
    compare,
    comparePlans,
    selectedRecord,
    setSelectedRecord,
    setCompare,
    setComparePlans,
    setCompareData,
    setCompareColumns,
    setShowCompareModal,
    classification,
    setClassification,
    openFilter,
    setOpenFilter,
    travelProfile,
    setTravelProfile,
    operator,
    setOperator,
    filteredData,
    setFilteredData,
  };

  return (
    <div>
      {isMobile ? (
        <MultiQuotationsHeader {...childProps} />
      ) : (
        <Affix offsetTop={HEADER_HEIGHT}>
          <MultiQuotationsHeader {...childProps} />
        </Affix>
      )}

      <MultiQuotationCompareModal
        filter={filter}
        compare={compare}
        showModal={showCompareModal}
        setShowModal={setShowCompareModal}
        compareData={compareData}
        compareColumns={compareColumns}
        openNotification={openNotification}
      />

      <MultiQuotationCheckoutModal
        documentContext={{ filter, listPlans }}
        showModal={showCheckoutModal}
        setShowModal={setShowCheckoutModal}
        record={selectedRecord}
        setSelectedRecord={setSelectedRecord}
        travellers={travellers}
        setTravellers={setTravellers}
        buyer={buyer}
        setBuyer={setBuyer}
      />

      {contextHolder}

      {listPlans?.length > 0 ? (
        <Row justify="center">
          <div className="mb-2 w-100">
            <QuotationsFilter {...childProps} dataListing={listPlans} />
          </div>
        </Row>
      ) : null}

      <Spin spinning={isWorking}>
        {filteredData?.length > 0 ? (
          filteredData.map(plan => (
            <div key={plan?._id} className={isMobile ? 'mt-12 mb-9' : 'mt-4 mb-9'}>
              <CardPlan {...childProps} plan={plan} />
            </div>
          ))
        ) : !isWorking ? (
          <Empty className="mt-25" />
        ) : null}
      </Spin>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default QuotationsInit;
